import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { hydrate, decrementCredits } from './boost.duck';

/**
 * Resolve the base URL for the BFF `/api/*` routes. Mirrors the logic in
 * `src/util/api.js#apiBaseUrl` so the dev-server-on-different-port path
 * still works.
 */
const getBaseUrl = () => {
  if (typeof window === 'undefined') return '/api/';
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  if (process.env.NODE_ENV === 'development' && port) {
    return `http://localhost:${port}/api/`;
  }
  return '/api/';
};

/**
 * RTK Query API for the listing-boost flow.
 *
 *   useFetchBoostsMutation()            – paginated fetch of caller's active
 *                                         boosts + global credits + Stripe
 *                                         prices. Hydrates the `boost` slice
 *                                         so dialogs read from Redux via
 *                                         {@link boost.duck} selectors
 *   useApplyBoostMutation()             – applies a boost; updates the slice
 *                                         on success (sets activeBoost, and
 *                                         decrements credits for pro_credit)
 *   useStartBoostCheckoutMutation()     – creates a Stripe PaymentIntent and
 *                                         returns the clientSecret for Elements
 */
export const boostApi = createApi({
  reducerPath: 'boostApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getBaseUrl(),
    credentials: 'include',
  }),
  endpoints: build => ({
    fetchBoosts: build.mutation({
      query: ({ page = 1, limit = 50 } = {}) => ({
        url: `boosts?page=${page}&limit=${limit}`,
        method: 'GET',
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(hydrate(data));
        } catch {
          /* surfaced via mutation state */
        }
      },
    }),

    applyBoost: build.mutation({
      query: body => ({ url: 'boosts/listing', method: 'POST', body }),
      // Note: the active-boost state flip is deferred to the dialog's "Done"
      // click (see BoostListingDialog.commitAndClose) so applying a boost does
      // not unmount the open confirmation while the user is reading it.
      async onQueryStarted({ source }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (source === 'pro_credit') dispatch(decrementCredits());
        } catch {
          /* surfaced via mutation state */
        }
      },
    }),

    startBoostCheckout: build.mutation({
      query: body => ({ url: 'boosts/checkout', method: 'POST', body }),
    }),
  }),
});

export const {
  useFetchBoostsMutation,
  useApplyBoostMutation,
  useStartBoostCheckoutMutation,
} = boostApi;

export default boostApi.reducer;
